import type { Simulation } from './simulation';
import type { WorldData, Route, MoveInput, Vec3 } from './types';
import { distance } from './types';
export function replayFrames(sim: Simulation, route: Route) {
    if (!route.initialState || !route.inputs?.length || route.physicsVersion !== sim.version)
        throw Error('This route needs a recording made with the current physics.');
    const frames = [route.initialState.slice()];
    sim.restore(route.initialState, 28);
    route.inputs.forEach((input, i) => { sim.step(28, input); if ((i + 1) % 6 === 0 || i === route.inputs!.length - 1)
        frames.push(sim.read(28).raw); });
    return frames;
}
const pause = () => new Promise<void>(r => setTimeout(r, 0));
const check = (signal?: AbortSignal) => { if (signal?.aborted)
    throw new DOMException('Analysis cancelled.', 'AbortError'); };
function rng(seed: number) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
export type Robustness = {
    seed: number;
    trialsPerProfile: number;
    profiles: {
        name: string;
        successes: number;
        trials: number;
        rate: number;
        interval: [
            number,
            number
        ];
        maxError: number;
        releaseMs: number;
        aimDegrees: number;
        pumpPercent: number;
        velocityPercent: number;
    }[];
    baseline: {
        error: number;
        grabs: number;
        tails: number;
        falls: number;
    };
    duration: number;
};
export async function robustness(sim: Simulation, route: Route, seed = 1234, trials = 32, signal?: AbortSignal, progress = (n: number) => { }): Promise<Robustness> {
    if (!route.initialState || !route.inputs?.length || !route.finalState || route.physicsVersion !== sim.version)
        throw Error('Record this route with the current physics before testing it.');
    trials = Math.max(4, Math.min(128, Math.floor(trials)));
    const random = rng(seed), inputs = route.inputs, initial = route.initialState, final = route.finalState;
    const run = (tape: MoveInput[], state = initial) => { sim.restore(state, 29); for (const input of tape)
        sim.step(29, input); return sim.read(29); };
    const baseline = run(inputs), result: Robustness = { seed, trialsPerProfile: trials, profiles: [], baseline: { error: distance(baseline.p, final.slice(0, 3)), grabs: baseline.grabs - initial[19], tails: baseline.tails - initial[20], falls: baseline.falls - initial[21] }, duration: inputs.length / 120 };
    if (result.baseline.error > .08 || baseline.grabs !== final[19])
        throw Error('The recording changed. Re-record or repair it before measuring robustness.');
    const releases: number[] = [];
    for (let i = 1; i < inputs.length; i++)
        if ((inputs[i - 1].buttons & 3) && (!(inputs[i].buttons & 3) || (inputs[i].buttons & 16)))
            releases.push(i);
    const profiles = [{ name: 'Beginner', release: 12, aim: 14, pump: .2, velocity: .12 }, { name: 'Standard', release: 6, aim: 7, pump: .1, velocity: .06 }, { name: 'Advanced', release: 3, aim: 3, pump: .05, velocity: .03 }];
    for (const profile of profiles) {
        let successes = 0, maxError = 0;
        for (let trial = 0; trial < trials; trial++) {
            check(signal);
            const angle = (random() * 2 - 1) * profile.aim * Math.PI / 180, scale = 1 + (random() * 2 - 1) * profile.pump, velocity = 1 + (random() * 2 - 1) * profile.velocity;
            const tape = inputs.map(i => { let x = (i.x * Math.cos(angle) - i.z * Math.sin(angle)) * scale, z = (i.x * Math.sin(angle) + i.z * Math.cos(angle)) * scale; const norm = Math.max(1, Math.hypot(x, z)); return { ...i, x: x / norm, z: z / norm }; });
            for (const release of releases) {
                const shift = Math.round((random() * 2 - 1) * profile.release);
                for (let i = Math.max(0, release + Math.min(0, shift)); i < Math.min(tape.length, release + Math.max(0, shift)); i++)
                    tape[i].buttons = shift < 0 ? inputs[release].buttons : inputs[release - 1].buttons;
            }
            const state = initial.slice();
            for (let i = 3; i < 6; i++)
                state[i] *= velocity;
            const end = run(tape, state), error = distance(end.p, final.slice(0, 3));
            maxError = Math.max(maxError, error);
            if (error < 1.5 && end.mode === final[15] && end.falls <= final[21])
                successes++;
            progress((result.profiles.length * trials + trial + 1) / (3 * trials));
            await pause();
        }
        const rate = successes / trials, z = 1.96, den = 1 + z * z / trials, center = (rate + z * z / (2 * trials)) / den, margin = z * Math.sqrt(rate * (1 - rate) / trials + z * z / (4 * trials * trials)) / den;
        result.profiles.push({ name: profile.name, successes, trials, rate, interval: [Math.max(0, center - margin), Math.min(1, center + margin)], maxError, releaseMs: profile.release / 120 * 1000, aimDegrees: profile.aim, pumpPercent: profile.pump * 100, velocityPercent: profile.velocity * 100 });
    }
    check(signal);
    return result;
}
export type GraphResult = {
    profile: number;
    nodes: {
        id: number;
        name: string;
        p: Vec3;
        tested: boolean;
        recovery: boolean;
    }[];
    edges: {
        from: number;
        to: number;
        seconds: number;
        tail: boolean;
    }[];
    warnings: {
        id: number;
        message: string;
    }[];
    tests: number;
    externalExits: number[];
};
/** Bounded, directed sampling. A missing edge means "not observed", never "impossible". */
export async function reachability(sim: Simulation, world: WorldData, origin: Vec3, assist = 1, limit = 24, signal?: AbortSignal, progress = (n: number) => { }): Promise<GraphResult> {
    const candidates = world.objects.filter(o => o.type !== 6).sort((a, b) => distance(a.a, origin) - distance(b.a, origin)).slice(0, Math.max(2, Math.min(48, limit)));
    const result: GraphResult = { profile: assist, nodes: candidates.map(o => ({ id: o.id, name: o.name, p: o.a.map((n, i) => (n + o.b[i]) / 2) as Vec3, tested: false, recovery: o.type === 0 || world.objects.some(r => r.type === 0 && r.a[1] < o.a[1] && Math.hypot(r.a[0] - o.a[0], r.a[2] - o.a[2]) < r.radius + 2) })), edges: [], warnings: [], tests: 0, externalExits: [] }, ids = new Set(candidates.map(o => o.id));
    for (let index = 0; index < candidates.length; index++) {
        const o = candidates[index], node = result.nodes[index];
        for (let direction = 0; direction < 8; direction++)
            for (const release of [36, 84]) {
                check(signal);
                const mid = node.p;
                sim.spawn(29, [mid[0], mid[1] + (o.type === 1 || o.type === 3 ? o.radius : 0) + .65, mid[2]]);
                if (o.type === 2)
                    sim.spawn(29, [o.b[0], o.b[1] - 1, o.b[2]]);
                for (let t = 0; t < 30; t++)
                    sim.step(29, { x: 0, z: 0, buttons: o.type === 2 ? 1 : 0, assist });
                const start = sim.read(29);
                if (start.support !== o.id && start.grab !== o.id)
                    continue;
                node.tested = true;
                const a = direction * Math.PI / 4;
                for (let tick = 0; tick < 240; tick++) {
                    const buttons = tick === 0 ? 4 : tick === release ? 16 : tick > release && tick < release + 24 ? 0 : 1;
                    sim.step(29, { x: Math.sin(a), z: Math.cos(a), buttons, assist });
                    const s = sim.read(29), target = s.grab >= 0 ? s.grab : s.support;
                    if (target >= 0 && target !== o.id) {
                        if(!ids.has(target)&&!result.externalExits.includes(o.id))result.externalExits.push(o.id);
                        if (ids.has(target) && !result.edges.some(e => e.from === o.id && e.to === target))
                            result.edges.push({ from: o.id, to: target, seconds: (tick + 1) / 120, tail: s.mode === 3 });
                        break;
                    }
                    if (s.p[1] < mid[1] - 18)
                        break;
                }
                result.tests++;
            }
        progress((index + 1) / candidates.length);
        await pause();
    }
    for (const node of result.nodes) {
        if (!node.tested)
            result.warnings.push({ id: node.id, message: 'No stable starting contact found for this sampling pattern.' });
        else if (!result.edges.some(e => e.from === node.id) && !result.externalExits.includes(node.id))
            result.warnings.push({ id: node.id, message: 'No outgoing link observed in the sampled launches.' });
        if (!node.recovery)
            result.warnings.push({ id: node.id, message: 'No nearby recovery deck detected below this contact.' });
        if (result.edges.some(e => e.to === node.id) && !result.edges.some(e => e.from === node.id) && !result.externalExits.includes(node.id))
            result.warnings.push({ id: node.id, message: 'Possible one-way trap: incoming links but no sampled exit.' });
    }
    check(signal);
    return result;
}
